/* assets/upsell-slider.js */
(function () {
  "use strict";

  const CORE = window.CordsUpsell;
  if (!CORE) {
    console.warn("[CordsUpsellSlider] Missing cords-upsell-core.js");
    return;
  }

  const SELECTORS = {
    root: "[data-upsell-root]",
    outer: "[data-upsell-outer]",
    sliderWindow: ".cart-popular-products-slider__window",
    wrapper: "[data-upsell-wrapper]",
    slide: ".cart-popular-products-slider__slide",
    prevBtn: ".cart-popular-products-slider__button--prev",
    nextBtn: ".cart-popular-products-slider__button--next",
    pagination: ".swiper-pagination",
    form: "[data-upsell-form]",
    variantSelect: "[data-variant-select]",
    variantId: "[data-variant-id]",
  };

  function uniq(arr) {
    return [...new Set(arr)];
  }

  function norm(s) {
    return String(s || "").trim().toLowerCase();
  }

  // "Color" in all store languages (Spanish is also "color")
  const COLOR_OPTION_NAMES = ["color", "colour", "färg", "farbe", "couleur", "colore"];

  function findOuterEl(el) {
    const sel = el.dataset.outer;
    if (sel) {
      const found = document.querySelector(sel);
      if (found) return found;
    }

    return (
      el.closest(SELECTORS.outer) ||
      el.parentElement?.closest?.(SELECTORS.outer) ||
      el.closest(".upsell-block") ||
      document.getElementById("cartImprovementsUpsell") || // backward compat
      null
    );
  }

  function showOuter(outer) {
    if (!outer) return;
    outer.removeAttribute("hidden");
    requestAnimationFrame(() => outer.classList.add("is-visible"));
  }

  function hideOuter(outer) {
    if (!outer) return;
    outer.classList.remove("is-visible");
    outer.setAttribute("hidden", "");
  }

  class CordsUpsellSlider extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
      this._onLoadInit = () => this.init();
      this.preferredColor = null;

      // ── PDP stability flags ──
      this._pdpShown = false;               // Once shown on PDP, never hide again
      this._suspendProductPageColorSync = false;
    }

    connectedCallback() {
      // Important: element might be inserted after window load (PDP AJAX / recommendations)
      if (document.readyState === "complete") {
        this.init();
      } else {
        window.addEventListener("load", this._onLoadInit, { once: true });
      }
    }

    disconnectedCallback() {
      window.removeEventListener("load", this._onLoadInit);
    }

    getConfig() {
      const ds = this.dataset;

      const context = (ds.context || "generic").toLowerCase();
      const placement = ds.upsellPlacement || context || "generic";

      const isCartContext = context === "cart" || placement === "cart_drawer";

      return {
        // source modes: manual | complementary | hybrid
        source: (ds.source || "manual").toLowerCase(),
        context,
        limit: CORE.toNumberOrNull(ds.limit) ?? 6,
        limitPerItem: CORE.toNumberOrNull(ds.limitPerItem) ?? 3,
        variantPicker: (ds.variantPicker || "dropdown").toLowerCase(),
        staticUpsell: (ds.staticUpsell || "false").toLowerCase() === "true",

        // Analytics / metadata
        upsellSource: ds.upsellSource || "upsell",
        upsellComponent: ds.upsellComponent || "upsell-slider",
        upsellPlacement: placement,
        triggerHandle: ds.triggerHandle || null,

        // Section rendering — PDP upsell also needs drawer sections
        //   so the cart drawer renders correctly after add-to-cart
        sections: ds.sections || (isCartContext || context === "pdp" ? "cart-drawer,cart-icon-bubble" : ""),
        sectionsUrl: ds.sectionsUrl || window.location.pathname,

        // Recommendations endpoint options
        recommendationsSectionId:
          ds.recommendationsSectionId ||
          (isCartContext ? "cart-complementary-recommendations" : "product-complementary-recommendations"),

        excludeCart: (ds.excludeCart || "true").toLowerCase() !== "false",

        // Anchor product
        productId: ds.productId ? String(ds.productId) : null,

        labelAdd: ds.labelAdd || "Add to cart",
        labelSoldout: ds.labelSoldout || "Sold out",
      };
    }

    async init() {
      // Guard: avoid re-running init concurrently.
      if (this._initializing) return;
      this._initializing = true;

      try {
        const sliderEl = this.querySelector(SELECTORS.sliderWindow);
        const wrapperEl = this.querySelector(SELECTORS.wrapper);
        if (!sliderEl || !wrapperEl) return;

        const cfg = this.getConfig();

        // Manual mode: slides are already present (Liquid rendered)
        if (cfg.source === "manual") {
          // PDP: transform Liquid-rendered slides into list layout
          if (cfg.context === "pdp") {
            this.transformSlidesToPdpList(wrapperEl, cfg);
          }

          this.toggleVisibility(wrapperEl);
          if (wrapperEl.children.length === 0) return;

          this.initSwiper(sliderEl, wrapperEl);
          this.bindVariantSelects();
          this.bindVariantPills();
          this.bindAddToCart();
          this.syncPreferredColorFromProductPage();
          this.bindProductPageVariantSync();
          return;
        }

        // Complementary/Hybrid: build slides dynamically
        await this.buildDynamicSlides(cfg, sliderEl, wrapperEl);

        // PDP: transform dynamically-built slides too (if renderPdpListItemHtml wasn't used)
        if (cfg.context === "pdp") {
          this.transformSlidesToPdpList(wrapperEl, cfg);
        }

        this.toggleVisibility(wrapperEl);
        if (wrapperEl.children.length === 0) return;

        this.initSwiper(sliderEl, wrapperEl);
        this.bindVariantSelects();
        this.bindVariantPills();
        this.bindAddToCart();
        this.syncPreferredColorFromProductPage();
        this.bindProductPageVariantSync();
      } catch (err) {
        console.error("[CordsUpsellSlider] init failed:", err);
      } finally {
        this._initializing = false;
        this._initialized = true;
      }
    }

    toggleVisibility(wrapperEl) {
      const slideCount = wrapperEl
        ? wrapperEl.querySelectorAll(SELECTORS.slide).length
        : 0;

      const outer = findOuterEl(this);
      const cfg = this.getConfig();

      // ── FIX: On PDP with static upsell, once shown never hide ──
      // This prevents the block from disappearing due to transient
      // re-init or race conditions after add-to-cart / drawer render.
      if (cfg.context === "pdp" && cfg.staticUpsell && this._pdpShown) {
        return; // Already shown, refuse to hide
      }

      if (slideCount === 0) {
        this.setAttribute("hidden", "");
        hideOuter(outer);
      } else {
        this.removeAttribute("hidden");
        showOuter(outer);

        // Mark as shown on PDP so we never hide again
        if (cfg.context === "pdp" && cfg.staticUpsell) {
          this._pdpShown = true;
        }
      }
    }

    async buildDynamicSlides(cfg, sliderEl, wrapperEl) {
      // Determine preferred color
      let storedColor = window.__cordsPreferredColor || null;

      const isCartContext =
        cfg.context === "cart" || cfg.upsellPlacement === "cart_drawer";

      if (isCartContext) {
        const cart = await this.fetchCart();
        const cartColor = this.getPreferredColorFromCart(cart);
        this.preferredColor = cartColor || storedColor || null;
        if (cartColor) window.__cordsPreferredColor = cartColor;

        const cartHandles = new Set((cart.items || []).map((i) => i.handle));
        const cartProductIds = (cart.items || []).map((i) => i.product_id);

        // frequency rank
        const freq = new Map();
        for (const pid of cartProductIds) {
          const handles = await this.fetchComplementaryHandles(pid, cfg.limitPerItem, cfg.recommendationsSectionId);
          for (const h of handles) {
            if (!h) continue;
            if (cfg.excludeCart && cartHandles.has(h)) continue;
            freq.set(h, (freq.get(h) || 0) + 1);
          }
        }

        let ranked = [...freq.entries()]
          .sort((a, b) => b[1] - a[1])
          .map(([h]) => h);

        if (cfg.source === "hybrid") {
          const manualHandles = this.readManualHandlesFromTemplate();
          for (const h of manualHandles) {
            if (!h) continue;
            if (cfg.excludeCart && cartHandles.has(h)) continue;
            if (!ranked.includes(h)) ranked.push(h);
          }
        }

        ranked = ranked.slice(0, cfg.limit);

        if (ranked.length === 0) {
          if (cfg.source === "hybrid") {
            wrapperEl.innerHTML = this.getManualTemplateSlidesHtml();
          } else {
            wrapperEl.innerHTML = "";
          }
          return;
        }

        wrapperEl.innerHTML = "";
        for (const handle of ranked) {
          const product = await this.fetchProductJson(handle);
          if (!product) continue;

          const slide = document.createElement("div");
          slide.className = "cart-popular-products-slider__slide";
          slide.innerHTML = this.renderSlideHtml(product, cfg.variantPicker, this.preferredColor, cfg);
          wrapperEl.appendChild(slide);
        }

        return;
      }

      // PDP context: recommend from current product id
      const productId = cfg.productId;
      if (!productId) {
        console.warn("[CordsUpsellSlider] Missing data-product-id for PDP complementary mode");
        wrapperEl.innerHTML = this.getManualTemplateSlidesHtml();
        return;
      }

      // On PDP we still fetch the cart for color preference, but never exclude cart items
      let cartHandles = new Set();
      if (cfg.excludeCart && cfg.context !== "pdp") {
        try {
          const cart = await this.fetchCart();
          cartHandles = new Set((cart.items || []).map((i) => i.handle));
          const cartColor = this.getPreferredColorFromCart(cart);
          this.preferredColor = cartColor || storedColor || null;
          if (cartColor) window.__cordsPreferredColor = cartColor;
        } catch {
          this.preferredColor = storedColor || null;
        }
      } else if (cfg.context === "pdp") {
        // PDP: fetch cart only for color preference, no handle exclusion
        try {
          const cart = await this.fetchCart();
          const cartColor = this.getPreferredColorFromCart(cart);
          this.preferredColor = cartColor || storedColor || null;
          if (cartColor) window.__cordsPreferredColor = cartColor;
        } catch {
          this.preferredColor = storedColor || null;
        }
      } else {
        this.preferredColor = storedColor || null;
      }

      const handles = await this.fetchComplementaryHandles(productId, cfg.limit, cfg.recommendationsSectionId);

      const ranked = handles.filter((h) => h && (cfg.context === "pdp" || !cfg.excludeCart || !cartHandles.has(h))).slice(0, cfg.limit);

      if (ranked.length === 0) {
        wrapperEl.innerHTML = this.getManualTemplateSlidesHtml();
        return;
      }

      wrapperEl.innerHTML = "";
      for (const handle of ranked) {
        const product = await this.fetchProductJson(handle);
        if (!product) continue;

        const slide = document.createElement("div");
        if (cfg.context === "pdp") {
          slide.className = "cart-popular-products-slider__slide upsell-pdp-list__item";
          slide.innerHTML = this.renderPdpListItemHtml(product, this.preferredColor, cfg);
        } else {
          slide.className = "cart-popular-products-slider__slide";
          slide.innerHTML = this.renderSlideHtml(product, cfg.variantPicker, this.preferredColor, cfg);
        }
        wrapperEl.appendChild(slide);
      }
    }

    // ---------- Color picking (from your existing logic) ----------
    getColorFromCartItem(item) {
      const colorKeys = COLOR_OPTION_NAMES;
      const owv = item?.options_with_values || [];
      const named = owv.find((o) => colorKeys.includes(norm(o?.name)));
      if (named?.value) return String(named.value).trim();

      const knownColors = new Set([
        "jet black",
        "sand",
        "pale pink",
        "electric orange",
        "mist white",
      ]);

      const vt = String(item?.variant_title || "");
      const parts = vt.split("/").map((p) => p.trim()).filter(Boolean);
      const fromTitle = parts.find((p) => knownColors.has(norm(p)));
      if (fromTitle) return fromTitle;

      const vo = item?.variant_options || [];
      const fromOptions = vo.map((v) => String(v).trim()).find((v) => knownColors.has(norm(v)));
      if (fromOptions) return fromOptions;

      return null;
    }

    getPreferredColorFromCart(cart) {
      const counts = new Map();
      for (const item of (cart?.items || [])) {
        const color = this.getColorFromCartItem(item);
        if (!color) continue;
        const key = String(color).trim();
        counts.set(key, (counts.get(key) || 0) + 1);
      }

      let best = null;
      let bestCount = 0;
      for (const [c, n] of counts.entries()) {
        if (n > bestCount) {
          best = c;
          bestCount = n;
        }
      }
      return best;
    }

    getColorOptionIndex(product) {
      const names = (product?.options || []).map((o) => norm(typeof o === "string" ? o : o?.name));
      const byName = names.findIndex((n) => COLOR_OPTION_NAMES.includes(n));
      if (byName !== -1) return byName;
      // Cords rule: Color is always option1
      if (names.length > 0 && names[0] !== "title") return 0;
      return -1;
    }

    /**
     * Find the first non-color option index suitable for pill display.
     * Returns -1 if none exists.
     */
    getPillOptionIndex(product, colorIdx) {
      const optionCount = (product?.options || []).length;
      if (optionCount === 0) return -1;
      if (optionCount === 1 && colorIdx === 0) return -1;
      for (let i = 0; i < optionCount; i++) {
        if (i !== colorIdx) return i;
      }
      return -1;
    }

    /**
     * Get a variant's value for a given option index (0, 1, or 2).
     */
    getOptionValue(variant, optionIdx) {
      if (optionIdx === 0) return variant.option1;
      if (optionIdx === 1) return variant.option2;
      if (optionIdx === 2) return variant.option3;
      return null;
    }

    /**
     * Shorten pill labels: "1 meters" → "1m", "2 meter" → "2m", etc.
     */
    shortenPillLabel(val) {
      if (!val) return val;
      const s = val.trim();
      // Match patterns like "1 meters", "2 meter", "1.5 m", "1m"
      const m = s.match(/^([\d.,]+)\s*(?:m|meters?|metres?|mètres?|metros?|metri|metro)\.?\s*$/i);
      if (m) return m[1] + "m";
      return s;
    }

    /**
     * Get unique non-color option values from product variants.
     * Returns [] if not pill-eligible (0 or 3+ values).
     */
    getPillValues(product, colorIdx) {
      const pillIdx = this.getPillOptionIndex(product, colorIdx);
      if (pillIdx === -1) return [];
      const seen = new Set();
      const values = [];
      for (const v of (product.variants || [])) {
        const val = this.getOptionValue(v, pillIdx);
        if (val && !seen.has(val)) {
          seen.add(val);
          values.push(val);
        }
      }
      return values.length >= 2 && values.length <= 2 ? values : [];
    }

    getMainVariantPicker() {
      const cfg = this.getConfig();
      if (cfg.context !== "pdp") return null;

      const sectionId = this.dataset.sectionId;
      if (!sectionId) return null;

      return (
        document.getElementById(`variant-radios-${sectionId}`) ||
        document.getElementById(`variant-selects-${sectionId}`) ||
        null
      );
    }

    getCurrentMainVariantObject() {
      const picker = this.getMainVariantPicker();
      if (!picker) return null;

      const input =
        document.querySelector(`#product-form-${picker.dataset.section} input[name="id"]`) ||
        document.querySelector("product-form form input[name='id']") ||
        null;

      const currentVariantId = input?.value ? String(input.value) : null;
      if (!currentVariantId) return null;

      const jsonEl = picker.querySelector('script[type="application/json"]');
      if (!jsonEl) return null;

      try {
        const variants = JSON.parse(jsonEl.textContent || "[]");
        return variants.find((v) => String(v.id) === currentVariantId) || null;
      } catch {
        return null;
      }
    }

    getCurrentProductPageVariantId() {
      const params = new URLSearchParams(window.location.search);
      const variantId = params.get("variant");
      return variantId ? String(variantId) : null;
    }

    getMainProductVariantInput() {
      return (
        document.querySelector('product-form form [name="id"]') ||
        document.querySelector('.product-form form [name="id"]') ||
        null
      );
    }

    getCurrentMainVariantId() {
      const fromUrl = this.getCurrentProductPageVariantId();
      if (fromUrl) return fromUrl;

      const input = this.getMainProductVariantInput();
      return input?.value ? String(input.value) : null;
    }

    /**
     * Extract the option label text from a fieldset.
     * Handles both picker types:
     *   variant-radios  → <legend class="product-form__group-name">Color…</legend>
     *   variant-selects → <div class="select-label"><div>Color:</div>…</div>
     */
    _getOptionNameFromFieldset(fieldset) {
      // variant-radios: legend.product-form__group-name
      const legend = fieldset.querySelector(".product-form__group-name");
      if (legend) {
        // textContent may include the selected value span (e.g. "Color Jet Black").
        // Only take the first text node / first child text to get the option name.
        const firstChild = legend.firstChild;
        if (firstChild && firstChild.nodeType === Node.TEXT_NODE) {
          const text = firstChild.textContent.trim();
          if (text) return text.toLowerCase();
        }
        // Fallback: full textContent
        return (legend.textContent || "").trim().toLowerCase();
      }

      // variant-selects: .select-label > div:first-child holds "Color:"
      const selectLabelDiv = fieldset.querySelector(".select-label > div:first-child");
      if (selectLabelDiv) {
        // Text is like "Color:" — strip the colon
        return (selectLabelDiv.textContent || "").replace(/:$/, "").trim().toLowerCase();
      }

      return "";
    }

    /**
     * Color fieldset detection that does not depend on the (translated) option name.
     * main-product.liquid marks color options via swatches:
     *   variant-radios  → fieldset.product-form__controls--colors
     *   variant-selects → .select-label--with-color
     */
    _isColorFieldset(fieldset, optionName, colorKeys) {
      if (fieldset.classList.contains("product-form__controls--colors")) return true;
      if (fieldset.querySelector(".select-label--with-color")) return true;
      return colorKeys.some((key) => optionName.includes(key));
    }

    getPreferredColorFromMainProduct() {
      const picker = this.getMainVariantPicker();
      if (!picker) return null;

      const colorKeys = COLOR_OPTION_NAMES;

      // Both variant-radios and variant-selects use <fieldset> per option.
      // The difference is how labels and inputs are structured inside.
      const fieldsets = Array.from(picker.querySelectorAll("fieldset"));

      for (const fieldset of fieldsets) {
        const optionName = this._getOptionNameFromFieldset(fieldset);

        if (!this._isColorFieldset(fieldset, optionName, colorKeys)) continue;

        // Found the color fieldset — get the selected value.
        // Works for both picker types: both use radio inputs.
        const checkedInput = fieldset.querySelector("input:checked");
        if (checkedInput?.value) {
          return String(checkedInput.value).trim();
        }

        // Fallback: native <select> element (rare but possible)
        const select = fieldset.querySelector("select");
        if (select) {
          const opt = select.options[select.selectedIndex];
          if (opt?.value) {
            return String(opt.value).trim();
          }
        }
      }

      // Fallback: resolve color from variant JSON data + fieldset index.
      // This catches edge cases where the checked input isn't found above.
      const variantObj = this.getCurrentMainVariantObject();
      if (variantObj?.options && fieldsets.length > 0) {
        for (let i = 0; i < fieldsets.length; i++) {
          const optionName = this._getOptionNameFromFieldset(fieldsets[i]);
          if (this._isColorFieldset(fieldsets[i], optionName, colorKeys)) {
            const colorValue = variantObj.options[i];
            if (colorValue) return String(colorValue).trim();
          }
        }
      }

      return null;
    }

    syncPreferredColorFromProductPage() {
      const cfg = this.getConfig();
      if (cfg.context !== "pdp") return;
      if (this._suspendProductPageColorSync) return;

      const color = this.getPreferredColorFromMainProduct();
      if (!color) return;

      this.preferredColor = color;
      window.__cordsPreferredColor = color;

      this.querySelectorAll(SELECTORS.form).forEach((form) => {
        const select = form.querySelector(SELECTORS.variantSelect);
        const hidden = form.querySelector(SELECTORS.variantId);
        if (!select || !hidden) return;

        const pref = norm(color);

        // If pills are active, try to match both color AND active pill value
        const item = form.closest(".upsell-pdp-item") || form.closest(SELECTORS.slide);
        const activePill = item?.querySelector(".upsell-variant-pill.is-active");
        const activePillValue = norm(activePill?.dataset?.pillValue || "");

        let match = null;

        // Priority 1: color + pill value match (prefer available, accept sold-out)
        if (activePillValue) {
          match = [...select.options].find(
            (o) =>
              norm(o?.dataset?.color) === pref &&
              norm(o?.dataset?.pillValue || "") === activePillValue &&
              !o.disabled
          );
          // Accept sold-out if no available match
          if (!match) {
            match = [...select.options].find(
              (o) =>
                norm(o?.dataset?.color) === pref &&
                norm(o?.dataset?.pillValue || "") === activePillValue
            );
          }
        }

        // Priority 2: color match only (prefer available, accept sold-out)
        if (!match) {
          match = [...select.options].find(
            (o) => norm(o?.dataset?.color) === pref && !o.disabled
          );
        }
        if (!match) {
          match = [...select.options].find(
            (o) => norm(o?.dataset?.color) === pref
          );
        }

        if (match) {
          select.value = match.value;
          hidden.value = match.value;
        } else {
          hidden.value = select.value;
        }

        this.updateUpsellImage(form, select);
        this.updateUpsellButtonState(form, select);
        this.updatePdpItemMeta(form, select);
        this.syncPillsToSelect(form, select);
      });
    }

    updatePdpItemMeta(form, select) {
      const item = form.closest(".upsell-pdp-item");
      if (!item) return;
      if (!select) return;

      const opt = select.options[select.selectedIndex];
      if (!opt) return;

      const colorKeys = new Set([
        "jet black", "sand", "pale pink", "electric orange", "mist white",
        "black", "white", "pink", "orange",
      ]);

      const colorName = (opt.dataset.color || "").trim();
      if (colorName) {
        const dot = item.querySelector(".upsell-pdp-item__color-dot");
        const label = item.querySelector(".upsell-pdp-item__color");
        if (dot) dot.style.backgroundColor = this._colorNameToHex(colorName);
        if (label) {
          const dotHtml = dot ? dot.outerHTML : "";
          label.innerHTML = dotHtml + this.escapeHtml(colorName);
        }
      }

      // Update variant meta (non-color parts like cable length)
      const variantTitle = opt.textContent?.trim() || "";
      const variantParts = variantTitle.split("/").map((p) => p.trim()).filter(Boolean);
      const variantMeta = variantParts.filter((p) => !colorKeys.has(norm(p))).join(", ");
      const variantEl = item.querySelector(".upsell-pdp-item__variant");
      if (variantEl) {
        variantEl.textContent = variantMeta;
      }

      const priceCents = opt.dataset.priceCents;
      const priceEl = item.querySelector(".upsell-pdp-item__price");
      if (priceEl) {
        if (opt.disabled) {
          priceEl.textContent = this.getConfig().labelSoldout || "Sold out";
          priceEl.classList.add("is-sold-out");
        } else if (priceCents) {
          priceEl.textContent = this.formatMoney(Number(priceCents));
          priceEl.classList.remove("is-sold-out");
        }
      }

      // Update + button sold-out state for PDP items
      const addBtn = item.querySelector(".upsell-pdp-item__add-btn");
      if (addBtn) {
        addBtn.disabled = !!opt.disabled;
        addBtn.classList.toggle("is-sold-out", !!opt.disabled);
      }
    }

    formatMoney(cents) {
      const amount = Number(cents);
      if (!Number.isFinite(amount)) return "";

      const currency =
        window.Shopify?.currency?.active ||
        window.Shopify?.currency?.iso_code ||
        "EUR";

      try {
        return new Intl.NumberFormat(document.documentElement.lang || "en", {
          style: "currency",
          currency,
        }).format(amount / 100);
      } catch {
        return `${(amount / 100).toFixed(2)} ${currency}`;
      }
    }

    // ---------- Swiper ----------
    initSwiper(sliderEl, wrapperEl) {
      const cfg = this.getConfig();

      // PDP: skip swiper entirely, show as stacked list
      if (cfg.context === "pdp") {
        this.removeAttribute("hidden");
        showOuter(findOuterEl(this));
        wrapperEl.querySelectorAll(SELECTORS.slide).forEach((el) => {
          el.classList.remove("swiper-slide");
        });
        // Add PDP list class to wrapper
        wrapperEl.classList.add("upsell-pdp-list");
        wrapperEl.classList.remove("swiper-wrapper");
        sliderEl.classList.remove("swiper");
        // Hide nav arrows and pagination
        const prevBtn = this.querySelector(SELECTORS.prevBtn);
        const nextBtn = this.querySelector(SELECTORS.nextBtn);
        const paginationEl = this.querySelector(SELECTORS.pagination);
        if (prevBtn) prevBtn.style.display = "none";
        if (nextBtn) nextBtn.style.display = "none";
        if (paginationEl) paginationEl.style.display = "none";

        // Sort: move sold-out items to the bottom
        const slides = [...wrapperEl.querySelectorAll(SELECTORS.slide)];
        const available = [];
        const soldOut = [];
        for (const slide of slides) {
          const btn = slide.querySelector(".upsell-pdp-item__add-btn");
          if (btn && (btn.disabled || btn.classList.contains("is-sold-out"))) {
            soldOut.push(slide);
          } else {
            available.push(slide);
          }
        }
        if (soldOut.length > 0 && available.length > 0) {
          for (const slide of [...available, ...soldOut]) {
            wrapperEl.appendChild(slide);
          }
        }

        return;
      }

      // Destroy previous swiper
      if (sliderEl?.swiper) {
        sliderEl.swiper.destroy(true, true);
      }

      wrapperEl.querySelectorAll(".swiper-slide-duplicate").forEach((el) => el.remove());
      wrapperEl.querySelectorAll(SELECTORS.slide).forEach((el) => el.removeAttribute("style"));

      sliderEl.classList.remove("swiper-initialized");
      sliderEl.removeAttribute("style");
      wrapperEl.removeAttribute("style");

      const slideCount = wrapperEl.children.length;

      const prevBtn = this.querySelector(SELECTORS.prevBtn);
      const nextBtn = this.querySelector(SELECTORS.nextBtn);
      const paginationEl = this.querySelector(SELECTORS.pagination);

      const showNav = slideCount > 1;
      if (prevBtn) prevBtn.style.display = showNav ? "" : "none";
      if (nextBtn) nextBtn.style.display = showNav ? "" : "none";
      if (paginationEl) paginationEl.style.display = showNav ? "" : "none";

      // 0 slides => hide everything
      if (slideCount === 0) {
        // ── FIX: On PDP static, don't hide if already shown ──
        const cfg = this.getConfig();
        if (cfg.context === "pdp" && cfg.staticUpsell && this._pdpShown) {
          return;
        }
        this.setAttribute("hidden", "");
        hideOuter(findOuterEl(this));
        return;
      }

      // 1 slide => show but don't init swiper
      if (slideCount === 1) {
        this.removeAttribute("hidden");
        showOuter(findOuterEl(this));
        wrapperEl.querySelectorAll(SELECTORS.slide).forEach((el) => el.classList.remove("swiper-slide"));
        return;
      }

      // 2+ slides => init swiper
      this.removeAttribute("hidden");
      showOuter(findOuterEl(this));

      sliderEl.classList.add("swiper");
      wrapperEl.classList.add("swiper-wrapper");
      wrapperEl.querySelectorAll(SELECTORS.slide).forEach((el) => el.classList.add("swiper-slide"));

      if (!window.Swiper) {
        console.warn("[CordsUpsellSlider] Swiper not found; showing static list.");
        return;
      }

      const swiperSettings = {
        slidesPerView: 1,
        spaceBetween: 8,
        speed: 400,
        allowTouchMove: true,
        loop: true,
        loopPreventsSliding: false,
        navigation: {
          nextEl: nextBtn,
          prevEl: prevBtn,
          disabledClass: "swiper-button-disabled",
        },
        pagination: {
          el: paginationEl,
          type: "bullets",
          clickable: true,
        },
      };

      new window.Swiper(sliderEl, swiperSettings);
    }

    // ---------- Variant select + add-to-cart ----------
    updateUpsellImage(form, select) {
      const slide = form.closest(SELECTORS.slide) || form.closest(".upsell-pdp-item");
      const img = slide?.querySelector(".cart-popular-products-slider__slide-image img")
        || slide?.querySelector(".upsell-pdp-item__image img");
      if (!img) return;

      const opt = select.options[select.selectedIndex];
      const url = opt?.dataset?.image;
      if (!url) return;

      img.src = url;
      img.removeAttribute("srcset");
      img.removeAttribute("sizes");
    }

    updateUpsellButtonState(form, select) {
      const btn = form.querySelector('button[name="add"]');
      const labelEl = form.querySelector(".button__label");
      if (!btn) return;

      const opt = select?.options?.[select.selectedIndex];
      const isSoldOut = !!opt?.disabled;

      const cfg = this.getConfig();
      const addLabel = cfg.labelAdd;
      const soldOutLabel = cfg.labelSoldout;

      btn.disabled = isSoldOut;
      btn.setAttribute("aria-disabled", String(isSoldOut));

      if (labelEl) {
        labelEl.textContent = isSoldOut ? soldOutLabel : addLabel;
        labelEl.setAttribute("data-label", isSoldOut ? soldOutLabel : addLabel);
      }

      btn.classList.toggle("is-sold-out", isSoldOut);
    }

    bindVariantSelects() {
      this.querySelectorAll(SELECTORS.form).forEach((form) => {
        const select = form.querySelector(SELECTORS.variantSelect);
        const hidden = form.querySelector(SELECTORS.variantId);
        if (!select || !hidden) return;

        // Apply preferred color if we have it and the select offers it
        // Prefer available variant, but accept sold-out for correct color display
        if (this.preferredColor) {
          const pref = norm(this.preferredColor);
          const match = [...select.options].find((o) => norm(o?.dataset?.color) === pref && !o.disabled)
            || [...select.options].find((o) => norm(o?.dataset?.color) === pref);
          if (match) select.value = match.value;
        }

        hidden.value = select.value;
        this.updateUpsellImage(form, select);
        this.updateUpsellButtonState(form, select);

        select.addEventListener("change", () => {
          hidden.value = select.value;

          this.updateUpsellImage(form, select);
          this.updateUpsellButtonState(form, select);
          this.updatePdpItemMeta(form, select);
          this.syncPillsToSelect(form, select);

          const opt = select.options[select.selectedIndex];
          const color = (opt?.dataset?.color || "").trim();
          if (color) {
            this.preferredColor = color;
            window.__cordsPreferredColor = color;
          }
        });
      });
    }

    // ---------- Variant pills ----------
    bindVariantPills() {
      this.querySelectorAll(".upsell-variant-pills").forEach((pillGroup) => {
        const item = pillGroup.closest(".upsell-pdp-item") || pillGroup.closest(SELECTORS.slide);
        if (!item) return;

        const form = item.querySelector("[data-upsell-form]");
        if (!form) return;

        const select = form.querySelector(SELECTORS.variantSelect);
        const hidden = form.querySelector(SELECTORS.variantId);
        if (!select || !hidden) return;

        pillGroup.querySelectorAll(".upsell-variant-pill").forEach((pill) => {
          pill.addEventListener("click", () => {
            // Update active state
            pillGroup.querySelectorAll(".upsell-variant-pill").forEach((p) => {
              p.classList.remove("is-active");
              p.setAttribute("aria-pressed", "false");
            });
            pill.classList.add("is-active");
            pill.setAttribute("aria-pressed", "true");

            const targetPillValue = norm(pill.dataset.pillValue || "");
            const preferredColor = norm(this.preferredColor || "");

            // Find best matching option: prefer (color + pill), fall back to (pill only)
            // Prefer available variants, but accept sold-out for correct color display
            let bestMatch = null;
            let fallback = null;
            let bestMatchSoldOut = null;
            let fallbackSoldOut = null;

            for (const opt of select.options) {
              const optPillValue = norm(opt.dataset?.pillValue || "");
              if (optPillValue !== targetPillValue) continue;

              const optColor = norm(opt.dataset?.color || "");
              const colorMatches = preferredColor && optColor === preferredColor;

              if (opt.disabled) {
                if (colorMatches && !bestMatchSoldOut) bestMatchSoldOut = opt;
                if (!fallbackSoldOut) fallbackSoldOut = opt;
              } else {
                if (colorMatches && !bestMatch) bestMatch = opt;
                if (!fallback) fallback = opt;
              }
            }

            const match = bestMatch || fallback || bestMatchSoldOut || fallbackSoldOut;
            if (match) {
              select.value = match.value;
              hidden.value = match.value;
              // Dispatch change to trigger all existing handlers
              select.dispatchEvent(new Event("change", { bubbles: true }));
            }
          });
        });
      });
    }

    /**
     * After a color sync or variant change, update which pill is visually active.
     */
    syncPillsToSelect(form, select) {
      const item = form.closest(".upsell-pdp-item") || form.closest(SELECTORS.slide);
      const pillGroup = item?.querySelector(".upsell-variant-pills");
      if (!pillGroup || !select) return;

      const selectedOpt = select.options[select.selectedIndex];
      const currentPillValue = norm(selectedOpt?.dataset?.pillValue || "");

      pillGroup.querySelectorAll(".upsell-variant-pill").forEach((p) => {
        const isActive = norm(p.dataset.pillValue || "") === currentPillValue;
        p.classList.toggle("is-active", isActive);
        p.setAttribute("aria-pressed", String(isActive));
      });
    }

    bindProductPageVariantSync() {
      const cfg = this.getConfig();
      if (cfg.context !== "pdp") return;
      if (this._boundProductVariantSync) return;
      this._boundProductVariantSync = true;

      const picker = this.getMainVariantPicker();
      if (!picker) return;

      // ── Primary: listen for change events on the variant picker ──
      picker.addEventListener("change", (event) => {
        const target = event.target;
        if (!target) return;
        // Don't react to changes within our own upsell forms
        if (target.closest("[data-upsell-form]")) return;

        window.setTimeout(() => {
          this.syncPreferredColorFromProductPage();
        }, 0);
      });

      // ── FIX: Secondary — listen for the hidden input[name="id"] change event ──
      // The theme's VariantSelects.updateVariantInput() dispatches a bubbling
      // change event on the hidden input. This catches variant changes that
      // don't bubble through the picker element (e.g., programmatic updates).
      const sectionId = this.dataset.sectionId;
      if (sectionId) {
        const variantInput =
          document.querySelector(`#product-form-${sectionId} input[name="id"]`);

        if (variantInput) {
          variantInput.addEventListener("change", () => {
            // Ignore during our own add-to-cart suspend window
            if (this._suspendProductPageColorSync) return;

            window.setTimeout(() => {
              this.syncPreferredColorFromProductPage();
            }, 50);
          });
        }
      }

      // ── Handle browser back/forward ──
      window.addEventListener("popstate", () => {
        window.setTimeout(() => {
          this.syncPreferredColorFromProductPage();
        }, 0);
      });
    }

    bindAddToCart() {
      const cfg = this.getConfig();

      this.querySelectorAll(SELECTORS.form).forEach((form) => {
        if (form.dataset.bound === "1") return;
        form.dataset.bound = "1";

        form.addEventListener("submit", async (e) => {
          e.preventDefault();

          const submitBtn =
            form.querySelector('button[type="submit"]') ||
            form.querySelector(".button");

          const variantInput = form.querySelector(SELECTORS.variantId);
          if (!variantInput?.value) return;

          const select = form.querySelector(SELECTORS.variantSelect);
          const opt = select?.options?.[select.selectedIndex];

          const metaId = String(Date.now()) + "_" + Math.random().toString(16).slice(2);
          const qty = 1;

          const isPdpStaticUpsell = cfg.context === "pdp" && cfg.staticUpsell;

          // ── FIX: Suspend color sync for entire add-to-cart + render cycle ──
          if (isPdpStaticUpsell) {
            this._suspendProductPageColorSync = true;
          }

          const body = new FormData();
          body.append("id", variantInput.value);
          body.append("quantity", String(qty));

          if (cfg.sections) {
            body.append("sections", cfg.sections);
            body.append("sections_url", cfg.sectionsUrl || window.location.pathname);
          }

          try {
            // PDP list: use compact button states
            const isPdpList = cfg.context === "pdp" && submitBtn.classList.contains("upsell-pdp-item__add-btn");
            if (isPdpList) {
              submitBtn.classList.add("is-loading");
            } else {
              window.CordsCartUI?.startButtonLoading(submitBtn);
            }
            window.CordsCartUI?.startFooterLoading();

            const res = await fetch(`${window.routes?.cart_add_url || "/cart/add"}`, {
              method: "POST",
              headers: { Accept: "application/json" },
              body,
            });

            const data = await res.json();

            if (!res.ok) {
              throw new Error(data?.description || data?.message || "Add to cart failed");
            }

            // Publish custom upsell event for GTM Pixel -> GA4
            try {
              const slide = form.closest(SELECTORS.slide);
              const title =
                slide?.querySelector(".cart-popular-products-slider__slide-title")?.textContent?.trim() ||
                slide?.querySelector(".upsell-pdp-item__title")?.textContent?.trim() ||
                null;

              const currency = CORE.getActiveCurrency();

              const linePriceCentsFromResponse = CORE.toNumberOrNull(data?.line_price);
              const priceCentsFromDom = CORE.toNumberOrNull(opt?.dataset?.priceCents);
              const linePriceCents = linePriceCentsFromResponse ?? priceCentsFromDom;

              const value = linePriceCents != null ? linePriceCents / 100 : null;
              const price = value;

              CORE.publishUpsellAddToCart({
                meta_id: metaId,
                source: cfg.upsellSource,
                component: cfg.upsellComponent,
                placement: cfg.upsellPlacement,
                trigger: form.dataset.triggerHandle || cfg.triggerHandle || null,

                color: (opt?.dataset?.color || "").trim() || null,
                variant_id: String(variantInput.value),
                item_name: title,

                currency,
                value,
                items: [
                  {
                    item_id: String(variantInput.value),
                    item_name: title,
                    item_variant: opt?.textContent?.trim() || null,
                    price,
                    quantity: qty,
                  },
                ],
              });
            } catch {
              // no-op
            }

            const shouldRenderDrawer =
              cfg.context === "cart" ||
              cfg.upsellPlacement === "cart_drawer" ||
              !!cfg.sections;

            // PDP list: stop loading spinner (item stays as-is, cart drawer handles the rest)
            if (isPdpList) {
              submitBtn.classList.remove("is-loading");
            }

            if (shouldRenderDrawer) {
              const drawer = document.querySelector("cart-drawer");
              if (drawer && typeof drawer.renderContents === "function" && data?.sections) {
                let activeElement = document.activeElement;
                const cartLink = document.querySelector("#cart-icon-bubble");
                if (cartLink) activeElement = cartLink;

                if (typeof drawer.setActiveElement === "function") {
                  drawer.setActiveElement(activeElement);
                }

                // Remove is-empty from cart-drawer element (same as product-form.js)
                // renderContents only clears is-empty on .drawer__inner, not the
                // cart-drawer element itself — which controls footer visibility via CSS
                if (drawer.classList.contains("is-empty")) {
                  drawer.classList.remove("is-empty");
                }

                drawer.renderContents(data);
              }
            }
          } catch (err) {
            console.error("Upsell add-to-cart failed:", err);
          } finally {
            const isPdpListBtn = cfg.context === "pdp" && submitBtn.classList.contains("upsell-pdp-item__add-btn");
            if (!isPdpListBtn) {
              window.CordsCartUI?.stopButtonLoading(submitBtn);
            }
            window.CordsCartUI?.stopFooterLoading();

            // ── FIX: Longer suspend window (1s) to cover the full
            //    cart drawer render cycle + any section re-render events ──
            if (isPdpStaticUpsell) {
              window.setTimeout(() => {
                this._suspendProductPageColorSync = false;
              }, 1000);
            }
          }
        });
      });
    }

    // ---------- Fetchers ----------
    async fetchCart() {
      const url = `${window.routes?.cart_url || "/cart"}.js`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      return await res.json();
    }

    async fetchComplementaryHandles(productId, limit, sectionId) {
      const base = window.Shopify?.routes?.root || "/";
      const url =
        `${base}recommendations/products?product_id=${productId}` +
        `&limit=${limit}` +
        `&intent=complementary` +
        `&section_id=${encodeURIComponent(sectionId || "cart-complementary-recommendations")}`;

      const res = await fetch(url, { headers: { Accept: "text/html" } });
      const htmlText = await res.text();
      const doc = new DOMParser().parseFromString(htmlText, "text/html");

      const anchors = [...doc.querySelectorAll('a[href*="/products/"]')];
      const handles = anchors
        .map((a) => this.extractHandle(a.getAttribute("href")))
        .filter(Boolean);


      return uniq(handles);
    }

    extractHandle(href) {
      if (!href) return null;
      const m = href.match(/\/products\/([^/?#]+)/);
      return m ? m[1] : null;
    }

    readManualHandlesFromTemplate() {
      const tpl = this.querySelector("template[data-manual-upsell-template]");
      if (!tpl) return [];
      const doc = new DOMParser().parseFromString(tpl.innerHTML, "text/html");
      const anchors = [...doc.querySelectorAll('a[href*="/products/"]')];
      const handles = anchors
        .map((a) => this.extractHandle(a.getAttribute("href")))
        .filter(Boolean);
      return uniq(handles);
    }

    getManualTemplateSlidesHtml() {
      const tpl = this.querySelector("template[data-manual-upsell-template]");
      return tpl ? tpl.innerHTML : "";
    }

    async fetchProductJson(handle) {
      try {
        const base = window.Shopify?.routes?.root || "/";
        const res = await fetch(`${base}products/${handle}.js`, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) return null;
        return await res.json();
      } catch {
        return null;
      }
    }

    // ---------- Transform existing Liquid-rendered slides into PDP list items ----------
    transformSlidesToPdpList(wrapperEl, cfg) {
      const slides = wrapperEl.querySelectorAll(SELECTORS.slide);
      if (!slides.length) return;

      const colorKeys = new Set([
        "jet black", "sand", "pale pink", "electric orange", "mist white",
        "black", "white", "pink", "orange",
      ]);

      slides.forEach((slide) => {
        // Skip if already transformed
        if (slide.querySelector(".upsell-pdp-item")) return;

        // Extract data from the existing Liquid-rendered slide
        const imgEl = slide.querySelector(".cart-popular-products-slider__slide-image img");
        const imgSrc = imgEl?.getAttribute("src") || "";
        const titleEl = slide.querySelector(".cart-popular-products-slider__slide-title");
        const titleText = titleEl?.textContent?.trim() || "";
        const titleHref = titleEl?.getAttribute("href") || "#";
        const form = slide.querySelector("[data-upsell-form]");
        const select = slide.querySelector("[data-variant-select]");
        const hiddenInput = slide.querySelector("[data-variant-id]");
        const currentVariantId = hiddenInput?.value || "";

        // Get form data attributes
        const triggerHandle = form?.dataset?.triggerHandle || "";
        const upsellPlacement = form?.dataset?.upsellPlacement || cfg.upsellPlacement || "";
        const upsellComponent = form?.dataset?.upsellComponent || cfg.upsellComponent || "";
        const upsellSource = form?.dataset?.upsellSource || cfg.upsellSource || "";

        // Determine current color + variant info from selected option
        const selectedOpt = select?.options?.[select.selectedIndex];
        const colorValue = selectedOpt?.dataset?.color || "";
        const colorHex = this._colorNameToHex(colorValue);

        // ── Price: try multiple extraction strategies ──
        // 1. From dedicated price element
        const priceEl = slide.querySelector(".cart-popular-products-slider__slide-price");
        let priceText = priceEl?.textContent?.trim() || "";

        // 2. From selected option data-price-cents
        if (!priceText && selectedOpt?.dataset?.priceCents) {
          const cents = Number(selectedOpt.dataset.priceCents);
          if (Number.isFinite(cents)) {
            priceText = this.formatMoney(cents);
          }
        }

        // 3. Regex scan of slide-info for price patterns
        if (!priceText) {
          const infoEl = slide.querySelector(".cart-popular-products-slider__slide-info");
          if (infoEl) {
            const allText = infoEl.textContent || "";
            const priceMatch = allText.match(/[€$£¥]\s*[\d.,]+\s*(?:EUR|USD|GBP|SEK|NOK|DKK)?|[\d.,]+\s*(?:EUR|USD|GBP|SEK|NOK|DKK)/i);
            if (priceMatch) priceText = priceMatch[0].trim();
          }
        }

        // Extract non-color variant info (e.g. "1 meters", "2m") from selected option text
        const variantTitle = selectedOpt?.textContent?.trim() || "";
        const variantParts = variantTitle.split("/").map((p) => p.trim()).filter(Boolean);
        const variantMeta = variantParts.filter((p) => !colorKeys.has(norm(p))).join(", ");

        // ── Pill detection from DOM select options ──
        // Extract unique non-color values across ALL options
        const allNonColorValues = [];
        const seenNonColor = new Set();
        if (select) {
          for (const opt of select.options) {
            const optTitle = opt.textContent?.trim() || "";
            const parts = optTitle.split("/").map((p) => p.trim()).filter(Boolean);
            const nonColor = parts.filter((p) => !colorKeys.has(norm(p)));
            for (const val of nonColor) {
              if (!seenNonColor.has(val)) {
                seenNonColor.add(val);
                allNonColorValues.push(val);
              }
            }
          }
        }
        const usePills = allNonColorValues.length >= 2 && allNonColorValues.length <= 2;

        // Determine active pill value from selected option
        const selectedNonColor = variantParts.filter((p) => !colorKeys.has(norm(p)));
        const activePillValue = usePills && selectedNonColor.length > 0 ? selectedNonColor[0] : "";

        // Product handle for async fetch
        const handle = this._extractHandleFromHref(titleHref);

        // Image HTML
        const imageHtml = imgSrc
          ? `<img src="${this.escapeHtml(imgSrc)}" loading="lazy" width="216" height="216" />`
          : "";

        // Rebuild variant select as hidden (preserves all options for color sync)
        let hiddenSelectHtml = "";
        if (select && select.options.length > 1) {
          const optionsHtml = [...select.options].map((opt) => {
            // Extract non-color value for data-pill-value
            const optParts = (opt.textContent?.trim() || "").split("/").map((p) => p.trim()).filter(Boolean);
            const optNonColor = optParts.filter((p) => !colorKeys.has(norm(p)));
            const pillValue = usePills && optNonColor.length > 0 ? optNonColor[0] : "";

            return `<option value="${opt.value}"
                      data-image="${this.escapeHtml(opt.dataset.image || "")}"
                      data-color="${this.escapeHtml(opt.dataset.color || "")}"
                      data-pill-value="${this.escapeHtml(pillValue)}"
                      data-price-cents="${opt.dataset.priceCents || ""}"
                      ${opt.disabled ? "disabled" : ""}
                      ${opt.selected ? "selected" : ""}>
                ${this.escapeHtml(opt.textContent.trim())}
              </option>`;
          }).join("");
          hiddenSelectHtml = `<select class="cart-upsell-variant-select upsell-pdp-item__hidden-select" data-variant-select aria-hidden="true" tabindex="-1">${optionsHtml}</select>`;
        }

        const isAvailable = hiddenInput?.value && !(selectedOpt?.disabled);
        const disabledAttr = isAvailable ? "" : "disabled";

        // ── Build pills or static variant text ──
        const pillsHtml = usePills
          ? `<span class="upsell-variant-pills">
              ${allNonColorValues.map((val) => {
                const isActive = val === activePillValue;
                const label = this.shortenPillLabel(val);
                return `<button type="button"
                          class="upsell-variant-pill${isActive ? " is-active" : ""}"
                          data-pill-value="${this.escapeHtml(val)}"
                          aria-pressed="${isActive}"
                        >${this.escapeHtml(label)}</button>`;
              }).join("")}
            </span>`
          : "";

        // Build meta line: product type · color · pills/variant
        // Product type will be inserted async, start with placeholder
        const metaParts = [];
        if (colorValue) {
          metaParts.push(`<span class="upsell-pdp-item__color"><span class="upsell-pdp-item__color-dot" style="background-color:${colorHex}"></span>${this.escapeHtml(colorValue)}</span>`);
        }
        if (usePills) {
          metaParts.push(pillsHtml);
        } else if (variantMeta) {
          metaParts.push(`<span class="upsell-pdp-item__variant">${this.escapeHtml(variantMeta)}</span>`);
        }
        const metaHtml = metaParts.join('<span class="upsell-pdp-item__meta-sep">·</span>');

        // Build the new list item HTML
        slide.className = "cart-popular-products-slider__slide upsell-pdp-list__item";
        slide.innerHTML = `
          <div class="upsell-pdp-item" data-product-handle="${this.escapeHtml(handle)}">
            <div class="upsell-pdp-item__image">
              <a class="full-unstyled-link cart-popular-products-slider__slide-link focus-inset" href="${this.escapeHtml(titleHref)}" aria-label="${this.escapeHtml(titleText)}"></a>
              ${imageHtml}
            </div>
            <div class="upsell-pdp-item__info">
              <a class="full-unstyled-link upsell-pdp-item__title focus-inset" href="${this.escapeHtml(titleHref)}">${this.escapeHtml(titleText)}</a>
              <div class="upsell-pdp-item__meta">
                <span class="upsell-pdp-item__type" data-product-type></span>
                ${metaHtml}
              </div>
            </div>
            ${isAvailable
              ? (priceText ? `<span class="upsell-pdp-item__price">${this.escapeHtml(priceText)}</span>` : `<span class="upsell-pdp-item__price" data-price-pending></span>`)
              : `<span class="upsell-pdp-item__price is-sold-out">${this.escapeHtml(this.getConfig().labelSoldout || "Sold out")}</span>`
            }
            <div class="card__add-to-cart upsell-pdp-item__action">
              <form class="form"
                novalidate
                data-upsell-form
                data-trigger-handle="${this.escapeHtml(triggerHandle)}"
                data-upsell-placement="${this.escapeHtml(upsellPlacement)}"
                data-upsell-component="${this.escapeHtml(upsellComponent)}"
                data-upsell-source="${this.escapeHtml(upsellSource)}">
                ${hiddenSelectHtml}
                <input type="hidden" name="id" value="${this.escapeHtml(currentVariantId)}" data-variant-id />
                <button class="upsell-pdp-item__add-btn focus-inset${isAvailable ? "" : " is-sold-out"}"
                        type="submit"
                        name="add"
                        aria-label="Add ${this.escapeHtml(titleText)}"
                        aria-haspopup="dialog"
                        aria-live="polite"
                        ${disabledAttr}>
                  <svg class="upsell-pdp-item__icon-plus" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true">
                    <line x1="8" y1="3" x2="8" y2="13" />
                    <line x1="3" y1="8" x2="13" y2="8" />
                  </svg>
                  <svg class="upsell-pdp-item__icon-check" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <polyline points="3,8.5 6.5,12 13,4" />
                  </svg>
                  <svg class="upsell-pdp-item__icon-spinner" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.8" stroke-dasharray="28" stroke-dashoffset="8" stroke-linecap="round" />
                  </svg>
                </button>
              </form>
            </div>
          </div>
        `;

        // ── Async: fetch product JSON for product_type and price fallback ──
        if (handle) {
          this._enrichPdpListItem(slide, handle, currentVariantId);
        }
      });
    }

    async _enrichPdpListItem(slide, handle, variantId) {
      try {
        const product = await this.fetchProductJson(handle);
        if (!product) return;

        // Insert product_type
        const typeEl = slide.querySelector("[data-product-type]");
        if (typeEl && product.product_type) {
          typeEl.textContent = product.product_type;
          // Add separator if there are sibling meta items after it
          const nextSibling = typeEl.nextElementSibling;
          if (nextSibling && !nextSibling.classList.contains("upsell-pdp-item__meta-sep")) {
            const sep = document.createElement("span");
            sep.className = "upsell-pdp-item__meta-sep";
            sep.textContent = "·";
            typeEl.after(sep);
          }
        }

        // Fill in price if it wasn't found from DOM
        const priceEl = slide.querySelector("[data-price-pending]");
        if (priceEl) {
          const variant = variantId
            ? (product.variants || []).find((v) => String(v.id) === String(variantId))
            : (product.variants || []).find((v) => v.available) || product.variants?.[0];

          if (variant && typeof variant.price === "number") {
            priceEl.textContent = this.formatMoney(variant.price);
            priceEl.removeAttribute("data-price-pending");
          }
        }
      } catch {
        // Silent fail — enrichment is progressive enhancement
      }
    }

    // ---------- PDP list item (no slider, no dropdown, compact + button) ----------
    renderPdpListItemHtml(product, preferredColor, cfg) {
      const title = this.escapeHtml(product.title);
      const url = product.url || `/products/${product.handle}`;
      const variants = product.variants || [];
      const preferred = norm(preferredColor);
      const colorIdx = this.getColorOptionIndex(product);

      const getVariantColor = (v) => {
        if (colorIdx === 0) return v.option1;
        if (colorIdx === 1) return v.option2;
        if (colorIdx === 2) return v.option3;
        return [v.option1, v.option2, v.option3].find((x) => norm(x) === preferred) || null;
      };

      const preferredVariant =
        preferred ? variants.find((v) => norm(getVariantColor(v)) === preferred) : null;
      const fallbackVariant = variants.find((v) => v.available) || variants[0];
      const chosenVariant = preferredVariant || fallbackVariant;
      const defaultVariantId = chosenVariant?.id || "";

      const getImgUrl = (img) => {
        if (!img) return null;
        if (typeof img === "string") return img;
        if (typeof img === "object" && img.src) return img.src;
        if (typeof img === "object" && img.url) return img.url;
        return null;
      };

      const productImgUrl =
        getImgUrl(product.featured_image) ||
        getImgUrl((product.images || [])[0]) ||
        "";
      const selectedImgUrl = getImgUrl(chosenVariant?.featured_image) || productImgUrl;

      const image = selectedImgUrl
        ? `<img src="${selectedImgUrl}" loading="lazy" width="216" height="216" />`
        : "";

      const isAvailable = !!chosenVariant?.available;
      const disabledAttr = isAvailable ? "" : "disabled";

      const colorValue = chosenVariant ? (getVariantColor(chosenVariant) || "") : "";
      const colorHex = this._colorNameToHex(colorValue);

      const priceHtml = isAvailable
        ? (typeof chosenVariant?.price === "number"
          ? `<span class="upsell-pdp-item__price">${this.escapeHtml(this.formatMoney(chosenVariant.price))}</span>`
          : "")
        : `<span class="upsell-pdp-item__price is-sold-out">${this.escapeHtml(cfg.labelSoldout || "Sold out")}</span>`;

      // ── Pill detection ──
      const pillValues = this.getPillValues(product, colorIdx);
      const usePills = pillValues.length > 0;
      const pillIdx = usePills ? this.getPillOptionIndex(product, colorIdx) : -1;

      // Build hidden variant select for color sync (hidden, no UI)
      const hiddenOptionsHtml = variants.length > 1
        ? `<select class="cart-upsell-variant-select upsell-pdp-item__hidden-select" data-variant-select aria-hidden="true" tabindex="-1">
            ${variants.map((v) => {
              const isSelected = String(v.id) === String(defaultVariantId);
              const optImg = getImgUrl(v.featured_image) || productImgUrl;
              const optColor = getVariantColor(v) || "";
              const optPillValue = pillIdx !== -1 ? (this.getOptionValue(v, pillIdx) || "") : "";
              return `<option value="${v.id}"
                        data-image="${this.escapeHtml(optImg)}"
                        data-color="${this.escapeHtml(optColor)}"
                        data-pill-value="${this.escapeHtml(optPillValue)}"
                        data-price-cents="${v.price}"
                        ${v.available ? "" : "disabled"}
                        ${isSelected ? "selected" : ""}>
                  ${this.escapeHtml(v.title)}
                </option>`;
            }).join("")}
          </select>`
        : "";

      // Product type from tags or product_type
      const productType = this.escapeHtml(product.product_type || "");

      // Extract non-color variant info (e.g. "1 meters") from chosen variant
      const colorKeys = new Set([
        "jet black", "sand", "pale pink", "electric orange", "mist white",
        "black", "white", "pink", "orange",
      ]);
      const variantTitle = chosenVariant?.title || "";
      const variantParts = variantTitle.split("/").map((p) => p.trim()).filter(Boolean);
      const variantMeta = variantParts.filter((p) => !colorKeys.has(norm(p))).join(", ");

      // ── Build pills or static variant text ──
      const chosenPillValue = pillIdx !== -1 ? this.getOptionValue(chosenVariant, pillIdx) : null;
      const pillsHtml = usePills
        ? `<span class="upsell-variant-pills" data-pill-option-idx="${pillIdx}">
            ${pillValues.map((val) => {
              const isActive = val === chosenPillValue;
              const label = this.shortenPillLabel(val);
              return `<button type="button"
                        class="upsell-variant-pill${isActive ? " is-active" : ""}"
                        data-pill-value="${this.escapeHtml(val)}"
                        aria-pressed="${isActive}"
                      >${this.escapeHtml(label)}</button>`;
            }).join("")}
          </span>`
        : "";

      // Build meta line
      const metaParts = [];
      if (productType) {
        metaParts.push(`<span class="upsell-pdp-item__type">${productType}</span>`);
      }
      if (colorValue) {
        metaParts.push(`<span class="upsell-pdp-item__color"><span class="upsell-pdp-item__color-dot" style="background-color:${colorHex}"></span>${this.escapeHtml(colorValue)}</span>`);
      }
      if (usePills) {
        metaParts.push(pillsHtml);
      } else if (variantMeta) {
        metaParts.push(`<span class="upsell-pdp-item__variant">${this.escapeHtml(variantMeta)}</span>`);
      }
      const metaHtml = metaParts.join('<span class="upsell-pdp-item__meta-sep">·</span>');

      return `
        <div class="upsell-pdp-item" data-product-url="${url}">
          <div class="upsell-pdp-item__image">
            <a class="full-unstyled-link cart-popular-products-slider__slide-link focus-inset" href="${url}" aria-label="${title}"></a>
            ${image}
          </div>
          <div class="upsell-pdp-item__info">
            <a class="full-unstyled-link upsell-pdp-item__title focus-inset" href="${url}">${title}</a>
            <div class="upsell-pdp-item__meta">
              ${metaHtml}
            </div>
          </div>
          ${priceHtml}
          <div class="card__add-to-cart upsell-pdp-item__action">
            <form class="form"
              novalidate
              data-upsell-form
              data-trigger-handle="${this.escapeHtml(cfg.triggerHandle || "")}"
              data-upsell-placement="${this.escapeHtml(cfg.upsellPlacement || "")}"
              data-upsell-component="${this.escapeHtml(cfg.upsellComponent || "")}"
              data-upsell-source="${this.escapeHtml(cfg.upsellSource || "")}">
              ${hiddenOptionsHtml}
              <input type="hidden" name="id" value="${defaultVariantId}" data-variant-id />
              <button class="upsell-pdp-item__add-btn focus-inset${isAvailable ? "" : " is-sold-out"}"
                      type="submit"
                      name="add"
                      aria-label="Add ${title}"
                      aria-haspopup="dialog"
                      aria-live="polite"
                      ${disabledAttr}>
                <svg class="upsell-pdp-item__icon-plus" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true">
                  <line x1="8" y1="3" x2="8" y2="13" />
                  <line x1="3" y1="8" x2="13" y2="8" />
                </svg>
                <svg class="upsell-pdp-item__icon-check" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <polyline points="3,8.5 6.5,12 13,4" />
                </svg>
                <svg class="upsell-pdp-item__icon-spinner" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.8" stroke-dasharray="28" stroke-dashoffset="8" stroke-linecap="round" />
                </svg>
              </button>
            </form>
          </div>
        </div>
      `;
    }

    _colorNameToHex(colorName) {
      const map = {
        "jet black": "#1a1a1a",
        "black": "#1a1a1a",
        "sand": "#c8b898",
        "pale pink": "#e8c4c4",
        "electric orange": "#e85b2a",
        "mist white": "#f5f1eb",
        "white": "#f5f1eb",
      };
      return map[norm(colorName)] || "#888888";
    }

    _extractHandleFromHref(href) {
      if (!href) return "";
      const m = href.match(/\/products\/([^/?#]+)/);
      return m ? m[1] : "";
    }

    // ---------- Slide HTML (slider mode — cart drawer etc.) ----------
    renderSlideHtml(product, variantPicker, preferredColor, cfg) {
      const title = this.escapeHtml(product.title);
      const url = product.url || `/products/${product.handle}`;

      const variants = product.variants || [];
      const preferred = norm(preferredColor);

      const colorIdx = this.getColorOptionIndex(product);

      const getVariantColor = (v) => {
        if (colorIdx === 0) return v.option1;
        if (colorIdx === 1) return v.option2;
        if (colorIdx === 2) return v.option3;
        return [v.option1, v.option2, v.option3].find((x) => norm(x) === preferred) || null;
      };

      const preferredVariant =
        preferred ? variants.find((v) => norm(getVariantColor(v)) === preferred) : null;

      const fallbackVariant = variants.find((v) => v.available) || variants[0];
      const chosenVariant = preferredVariant || fallbackVariant;
      const defaultVariantId = chosenVariant?.id || "";

      const getImgUrl = (img) => {
        if (!img) return null;
        if (typeof img === "string") return img;
        if (typeof img === "object" && img.src) return img.src;
        if (typeof img === "object" && img.url) return img.url;
        return null;
      };

      const productImgUrl =
        getImgUrl(product.featured_image) ||
        getImgUrl((product.images || [])[0]) ||
        "";

      const selectedImgUrl = getImgUrl(chosenVariant?.featured_image) || productImgUrl;

      const image = selectedImgUrl
        ? `<img src="${selectedImgUrl}" loading="lazy" width="216" height="216" />`
        : "";

      const hasDropdown = variantPicker === "dropdown" && variants.length > 1;

      const optionsHtml = hasDropdown
        ? `<select class="cart-upsell-variant-select" data-variant-select>
            ${variants
              .map((v) => {
                const isSelected = String(v.id) === String(defaultVariantId);
                const sold = v.available ? "" : ` - ${cfg.labelSoldout || "Sold out"}`;
                const optImg = getImgUrl(v.featured_image) || productImgUrl;
                const optColor = getVariantColor(v) || "";

                return `<option value="${v.id}"
                                data-image="${this.escapeHtml(optImg)}"
                                data-color="${this.escapeHtml(optColor)}"
                                data-price-cents="${v.price}"
                                ${v.available ? "" : "disabled"}
                                ${isSelected ? "selected" : ""}>
                          ${this.escapeHtml(v.title)}${this.escapeHtml(sold)}
                        </option>`;
              })
              .join("")}
          </select>`
        : "";

      const isAvailable = !!chosenVariant?.available;
      const label = isAvailable ? cfg.labelAdd : cfg.labelSoldout;
      const disabledAttr = isAvailable ? "" : "disabled";

      const priceHtml =
        typeof chosenVariant?.price === "number"
          ? `<div class="cart-popular-products-slider__slide-price">${this.escapeHtml(
              this.formatMoney(chosenVariant.price)
            )}</div>`
          : "";

      return `
        <div class="cart-popular-products-slider__slide-details">
          <div class="cart-popular-products-slider__slide-image">
            <a class="full-unstyled-link cart-popular-products-slider__slide-link focus-inset" href="${url}" aria-label="Product link"></a>
            ${image}
          </div>
          <div class="cart-popular-products-slider__slide-info">
            <a class="full-unstyled-link cart-popular-products-slider__slide-title focus-inset" href="${url}">${title}</a>
            ${priceHtml}
          </div>
        </div>
        <div class="card__add-to-cart cart-popular-products-slider__slide-button">
          <form class="form"
            novalidate
            data-upsell-form
            data-trigger-handle="${this.escapeHtml(cfg.triggerHandle || "")}"
            data-upsell-placement="${this.escapeHtml(cfg.upsellPlacement || "")}"
            data-upsell-component="${this.escapeHtml(cfg.upsellComponent || "")}"
            data-upsell-source="${this.escapeHtml(cfg.upsellSource || "")}">
            ${optionsHtml}
            <input type="hidden" name="id" value="${defaultVariantId}" data-variant-id />
            <button class="button button--primary focus-inset"
                    type="submit"
                    name="add"
                    aria-haspopup="dialog"
                    aria-live="polite"
                    ${disabledAttr}>
              <div class="button__content">
                <span class="button__label" data-label="${this.escapeHtml(label)}">
                  ${this.escapeHtml(label)}
                </span>
              </div>

              <span class="cart-popular-products-slider__slide-spinner hidden" aria-hidden="true">
                <svg
                  aria-hidden="true"
                  focusable="false"
                  class="icon icon-spinner"
                  viewBox="0 0 66 66"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    class="path"
                    fill="none"
                    stroke-width="6"
                    cx="33"
                    cy="33"
                    r="30"
                  ></circle>
                </svg>
              </span>

              <span class="animated-button-circle"></span>
            </button>
          </form>
        </div>
      `;
    }

    escapeHtml(str) {
      return String(str || "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
    }
  }

  if (!customElements.get("cart-popular-products-slider")) {
    customElements.define("cart-popular-products-slider", CordsUpsellSlider);
  }

  window.CordsUpsellSlider = {
    initAll(root) {
      const scope = root && root.querySelectorAll ? root : document;
      scope
        .querySelectorAll("cart-popular-products-slider")
        .forEach((el) => el.init && el.init());
    },
  };
})();